import { useEffect, useState } from "react";

import { TextArea, Select, DatePicker } from "@king-ict/eupisi-ui/components";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { DEFAULT_REQUEST } from "./constants";
import { getInstitutionById } from "../../api/Institution/InstitutionApi";
import {
  getRequest,
  getMedicalExamination,
  saveMedicalExamination,
} from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestHeader,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { ValidationFailure, Role } from "../../models";
import { CommitteeRoleEnum } from "../../models/Committee";
import { Institution } from "../../models/Institution";
import { Request, MedicalExamination } from "../../models/Request";
import { requestAdminDetailsUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";

export function MedicalExaminationEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const createDefaultDecision = (): MedicalExamination => {
    return {
      id: 0,
      requestId: +id,
    };
  };
  const [item, setItem] = useState<Request>(DEFAULT_REQUEST);
  const [exam, setExam] = useState<MedicalExamination>(createDefaultDecision());
  const [primaryInstitution, setPrimaryInstitution] = useState<
    Institution | undefined
  >();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [isDirty, setIsDirty] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      setIsEditing(true); // Assume we don't get one.
      if (id && id !== "0") {
        var result = await getMedicalExamination(+id);
        if (result) {
          setExam(result);
          setIsEditing(false);
          if (result.request) {
            setItem(result.request);
          }
        } else {
          var request = await getRequest(+id);
          if (request) {
            setItem(request);
          }
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    (async () => {
      if (
        item.institutionId &&
        item.schoolYearId > 0 &&
        (!primaryInstitution || primaryInstitution.id !== item.institutionId)
      ) {
        setPrimaryInstitution(
          await getInstitutionById(item.institutionId, item.schoolYearId)
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.institutionId, item.schoolYearId]);

  const canSave = (): boolean => {
    return isEditing;
  };
  const handleEditingClick = () => {
    setIsEditing(true);
  };
  const handleIsDoneChange = (val?: boolean) => {
    const newExamination: MedicalExamination = {
      ...exam,
      isDone: val,
    };
    handleUpdate(newExamination);
  };
  const handleExaminationDateChange = (val?: Date) => {
    const newExamination: MedicalExamination = {
      ...exam,
      examinationDate: val,
    };
    handleUpdate(newExamination);
  };
  const handleNoteChange = (val?: string) => {
    const newExamination: MedicalExamination = {
      ...exam,
      note: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(newExamination);
  };

  const handleUpdate = (val: MedicalExamination) => {
    setExam(val);
    dispatch(setIsEdited());
    setIsDirty(true);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={
          !isEditing &&
          (auth.currentRole?.role === Role.CAS ||
            auth.currentRole?.role === Role.Helpdesk ||
            auth.currentRole?.role === Role.InstitutionAdministrator ||
            auth.currentRole?.role === Role.InstitutionPrincipal ||
            (item.institutionCommitteeId &&
              auth
                .getCommitteeMembershipByCommitteeId(
                  item.institutionCommitteeId
                )
                ?.some(
                  (v) =>
                    v.isPresident ||
                    v.committeeRoleId === CommitteeRoleEnum.Physician
                )))
            ? "show"
            : "hide"
        }
        pageTitle={t("medicalExamination.pageTitle")}
        onClickEdit={handleEditingClick}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard cardTitle={t("common.requestModel.medicalExamination")}>
        <Row>
          <Col md={3}>{t("medicalExamination.isDone")}</Col>
          <Col md={9}>
            <Select
              value={
                exam.isDone !== undefined
                  ? exam.isDone === true
                    ? 2
                    : 1
                  : undefined
              }
              selectData={codeBookToSelectData([
                { id: 1, name: t("common.no") },
                { id: 2, name: t("common.yes") },
              ])}
              emptyOption={{
                text: "--" + t("common.select") + "--",
                value: undefined,
              }}
              onChange={(e) =>
                handleIsDoneChange(
                  e.target.value !== undefined
                    ? e.target.value === 2
                      ? true
                      : false
                    : undefined
                )
              }
              disabled={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("IsDone"))
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={3}>{t("medicalExamination.examinationDate")}</Col>
          <Col md={9}>
            <DatePicker
              value={exam?.examinationDate}
              onChange={(e) =>
                handleExaminationDateChange(
                  e.target.value ? new Date(e.target.value) : undefined
                )
              }
              minYear={new Date().getFullYear()}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("ExaminationDate")
                )?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={3}>{t("medicalExamination.note")}</Col>
          <Col md={9}>
            <TextArea
              ariaLabel={t("medicalExamination.note")}
              value={exam?.note}
              onChange={(e) => handleNoteChange(e.target.value)}
              readonly={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("Note"))?.message
              }
            />
          </Col>
        </Row>
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={canSave() ? "show" : "hide"}
        onClickCancel={history.goBack}
        handleSave={async () => saveMedicalExamination(exam)}
        setSaveErrors={setErrors}
        redirectUrlSaveOK={() => requestAdminDetailsUrl(+id)}
        showCustom={false}
      />
    </ContentArea>
  );
}
